import * as React from "react"
import { useMembership } from "../hooks/use-membership"
import { Paper, Tab, Tabs, type Theme, makeStyles } from "@material-ui/core"
import { useMembershipPermission } from "../../permissions/hooks/use-memberhip-permission"
import { useOptions } from "../../options/hooks/use-options"
import { useSettingsPageTranslations } from "../hooks/use-settings-page-translations"
import { MembershipBasicInfoForm } from "./MembershipBasicInfoForm"
import { SettingsPageHeader } from "./SettingsPageHeader"
import { MembershipPermissionManager } from "./MembershipPermissionManager"
import { Alert } from "@material-ui/lab"
import { Award } from "react-feather"
import { useRouter } from "../../hooks"

const MembershipSettingsView = ({ membershipId }: Props) => {
  const classes = useStyles()
  const router = useRouter()
  const query = router.query as { tab?: string }
  const { membership } = useMembership(membershipId)
  const translations = useSettingsPageTranslations({
    name: membership.fullname,
    member: membership.fullname,
  })
  const { customerOptions, membershipOptions, supplierOptions } = useOptions(membership.orgId)
  const { permission, ...permissionActions } = useMembershipPermission(membershipId)
  const isLimitedOrStandardMember = membership.isLimitedMember || membership.isStandardMember
  const shouldShowMembershipPermissionManager = membership.canUpdatePermissions && isLimitedOrStandardMember
  const currentTab = query.tab === "permissions" && shouldShowMembershipPermissionManager ? 1 : 0

  const handleChangeTab = (_: React.ChangeEvent<{}>, newValue: number) => {
    const currentSearch = new URLSearchParams(router.location.search)
    currentSearch.set("tab", newValue === 1 ? "permissions" : "basic")
    router.push({ search: currentSearch.toString() })
  }

  return (
    <div className={classes.container}>
      <SettingsPageHeader membership={membership} translations={translations} />
      {membership.isOwner ? (
        <div className={classes.ownerInfoContainer}>
          <Alert severity="success" icon={<Award size={20} />} variant="standard">
            {translations.ownerAccessInfo}
          </Alert>
        </div>
      ) : null}

      <Tabs
        variant="scrollable"
        value={currentTab}
        onChange={handleChangeTab}
        indicatorColor="primary"
        className={classes.tabs}
      >
        <Tab className={classes.tab} data-test="basic-settings-tab" disableRipple label={translations.basicInfo} />
        {shouldShowMembershipPermissionManager && (
          <Tab
            className={classes.tab}
            data-test="permissions-tab"
            disableRipple
            label={translations.permissionsTabLabel}
          />
        )}
      </Tabs>

      <TabPanel currentTab={currentTab} index={0}>
        <Paper variant="outlined" style={{ padding: `20px 16px` }}>
          <MembershipBasicInfoForm
            membership={membership}
            canUpdateBasicInfo={membership.canUpdateDetails}
            translations={translations}
          />
        </Paper>
      </TabPanel>

      {shouldShowMembershipPermissionManager && (
        <TabPanel currentTab={currentTab} index={1}>
          <Paper variant="outlined" style={{ padding: `20px 16px` }}>
            <MembershipPermissionManager
              membership={membership}
              permission={permission}
              copyPermissions={permissionActions.copyPermissions}
              setAbilitiesInResources={permissionActions.updateAbilitiesInResources}
              setAssociations={permissionActions.updateAssociations}
              updateAbilities={(updates) => permissionActions.updateAbilities({ orgId: membership.orgId, ...updates })}
              customerOptions={customerOptions}
              supplierOptions={supplierOptions}
              membershipOptions={membershipOptions}
              translations={translations}
            />
          </Paper>
        </TabPanel>
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    margin: theme.spacing(0.75, "auto"),
    maxWidth: 750,
    padding: theme.spacing(1.5),
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tab: {
    minWidth: 120,
  },
  tabPanel: {
    padding: theme.spacing(3, 0),
  },
  ownerInfoContainer: {
    marginTop: theme.spacing(-1.5),
    marginBottom: theme.spacing(3),
    "& .MuiAlert-message": {
      fontWeight: 500,
    },
    "& .MuiAlert-icon": {
      marginTop: theme.spacing(0.5),
      color: theme.palette.common.black,
    },
  },
}))

function TabPanel({ children, currentTab, index }: TabPanelProps) {
  const classes = useStyles()
  const hidden = currentTab !== index

  return (
    <div role="tabpanel" hidden={hidden} aria-hidden={hidden} className={classes.tabPanel}>
      {currentTab === index && children}
    </div>
  )
}

export default MembershipSettingsView

type Props = { membershipId: string }

type TabPanelProps = {
  children: React.ReactNode
  index: number
  currentTab: number
}
